import React from 'react';
import { HaulingSettings, HaulingCategoryRate, HaulingDiscount } from '../types';
import { computeTravelFee } from '../utils/haulingFee';

interface HaulingFeePreviewProps {
  distance: number; // Distance in miles
  categoryId: number;
  settings: HaulingSettings;
  categoryRates: HaulingCategoryRate[];
  discounts?: HaulingDiscount[];
  /** Home service shows the flat travel fee instead of the category hauling fee (the DB trigger's rule). */
  requestLocation?: 'home' | 'shop' | null;
  className?: string;
}

export const HaulingFeePreview: React.FC<HaulingFeePreviewProps> = ({
  distance,
  categoryId,
  settings,
  categoryRates,
  discounts = [],
  requestLocation = null,
  className = '',
}) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (requestLocation === 'home') {
    const travel = computeTravelFee(distance, settings);
    if (travel === null) return null;
    const free = settings.travel_free_miles ?? 0;
    const travelBase = settings.travel_base_fee ?? 0;
    const perMile = settings.travel_per_mile_rate ?? 0;
    return (
      <div className={`hauling-fee-preview ${className}`}>
        <div className="hauling-fee-preview__header">
          <strong>Estimated Travel Fee</strong>
        </div>
        <div className="hauling-fee-preview__content">
          {travelBase > 0 && (
            <div className="hauling-fee-preview__line">
              <span>Base fee:</span>
              <span>{formatCurrency(travelBase)}</span>
            </div>
          )}
          <div className="hauling-fee-preview__line">
            <span>{(distance - free).toFixed(1)} miles beyond {free} mi × {formatCurrency(perMile)}/mi:</span>
            <span>{formatCurrency((distance - free) * perMile)}</span>
          </div>
          <div className="hauling-fee-preview__line hauling-fee-preview__line--total">
            <span>Total Travel Fee:</span>
            <span>{formatCurrency(travel)}</span>
          </div>
        </div>
        <div className="hauling-fee-preview__note">
          <small>This estimate will be added to your service cost</small>
        </div>
      </div>
    );
  }

  // Return null if hauling is disabled
  if (!settings.enabled || distance <= 0) {
    return null;
  }

  // Find the category rate
  const categoryRate = categoryRates.find(r => r.category_id === categoryId);
  if (!categoryRate) {
    return null;
  }

  // Calculate chargeable distance
  const minimumDistance = settings.minimum_distance_miles || 0;
  const chargeableDistance = Math.max(0, distance - minimumDistance);

  // Base calculation
  const baseFee = settings.base_fee || 0;
  const perMileCharge = chargeableDistance * categoryRate.per_mile_rate;
  let subtotal = baseFee + perMileCharge;
  let cost = subtotal;

  // Track applied discounts
  const appliedDiscounts: { name: string; amount: number }[] = [];

  // Apply applicable discounts
  if (discounts && discounts.length > 0) {
    const now = new Date();
    for (const discount of discounts) {
      // Check if discount is active and within date range
      if (!discount.active) continue;
      if (discount.start_date && new Date(discount.start_date) > now) continue;
      if (discount.end_date && new Date(discount.end_date) < now) continue;

      // Check minimum distance requirement
      if (discount.min_distance && distance < discount.min_distance) continue;

      // Check category filter
      if (discount.category_ids && discount.category_ids.length > 0) {
        if (!discount.category_ids.includes(categoryId)) continue;
      }

      // Calculate discount amount
      let discountAmount = 0;
      if (discount.discount_type === 'percentage') {
        discountAmount = cost * (discount.amount / 100);
        cost *= (1 - discount.amount / 100);
      } else {
        discountAmount = discount.amount;
        cost -= discount.amount;
      }

      appliedDiscounts.push({
        name: discount.name,
        amount: Math.round(discountAmount * 100) / 100,
      });
    }
  }

  const finalCost = Math.max(0, Math.round(cost * 100) / 100);

  // Don't show if final cost is 0
  if (finalCost === 0) {
    return null;
  }

  return (
    <div className={`hauling-fee-preview ${className}`}>
      <div className="hauling-fee-preview__header">
        <strong>Estimated Hauling Fee</strong>
      </div>
      <div className="hauling-fee-preview__content">
        {baseFee > 0 && (
          <div className="hauling-fee-preview__line">
            <span>Base fee:</span>
            <span>{formatCurrency(baseFee)}</span>
          </div>
        )}
        {chargeableDistance > 0 && (
          <div className="hauling-fee-preview__line">
            <span>{chargeableDistance.toFixed(1)} miles × {formatCurrency(categoryRate.per_mile_rate)}/mi:</span>
            <span>{formatCurrency(perMileCharge)}</span>
          </div>
        )}
        {appliedDiscounts.length > 0 && (
          <>
            <div className="hauling-fee-preview__line">
              <span>Subtotal:</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {appliedDiscounts.map((discount, index) => (
              <div key={index} className="hauling-fee-preview__line hauling-fee-preview__line--discount">
                <span>{discount.name}:</span>
                <span>-{formatCurrency(discount.amount)}</span>
              </div>
            ))}
          </>
        )}
        <div className="hauling-fee-preview__line hauling-fee-preview__line--total">
          <span>Total Hauling Fee:</span>
          <span>{formatCurrency(finalCost)}</span>
        </div>
      </div>
      <div className="hauling-fee-preview__note">
        <small>This estimate will be added to your service cost</small>
      </div>
    </div>
  );
};
